import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query'
import { calendarApi } from '@/api/calendar'
import type {
  CalendarEvent,
  CreateEventPayload,
  UpdateEventPayload,
  EventTypeFilter
} from '@/types/calendar'

/**
 * Query hook for fetching team calendar events
 * Implements caching, automatic refetching, and optimistic updates
 */
export function useCalendarEvents(
  startDate: string,
  endDate: string,
  filters?: EventTypeFilter[]
): UseQueryResult<CalendarEvent[], Error> {
  return useQuery({
    queryKey: ['calendar-events', startDate, endDate, filters],
    queryFn: () => calendarApi.getMyTeamEvents({ startDate, endDate, filters }),
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 5, // 5 minutes (formerly cacheTime in React Query v4)
    refetchOnWindowFocus: true,
    retry: 1
  })
}

/**
 * Mutation hook for creating calendar events
 * Implements optimistic updates with rollback on error
 */
export function useCreateEvent(): UseMutationResult<
  CalendarEvent,
  Error,
  CreateEventPayload
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: calendarApi.createEvent,
    onMutate: async (newEvent) => {
      // Cancel outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: ['calendar-events'] })

      // Snapshot current value for rollback
      const previousEvents = queryClient.getQueriesData({ queryKey: ['calendar-events'] })

      // Optimistically update all calendar event queries
      queryClient.setQueriesData(
        { queryKey: ['calendar-events'] },
        (old: CalendarEvent[] | undefined) => {
          if (!old) return old

          // Create temporary event for optimistic UI
          const tempEvent: CalendarEvent = {
            eventId: 'temp-' + Date.now(),
            tenantId: 'pending',
            type: newEvent.type,
            employeeId: newEvent.employeeId,
            employeeName: 'Loading...',
            department: '',
            title: newEvent.title,
            startDate: newEvent.startDate,
            endDate: newEvent.endDate,
            allDay: newEvent.allDay,
            status: 'pending',
            description: newEvent.description,
            createdBy: 'current-user',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }

          return [...old, tempEvent]
        }
      )

      return { previousEvents }
    },
    onError: (_err, _newEvent, context) => {
      // Rollback optimistic update on error
      if (context?.previousEvents) {
        context.previousEvents.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSuccess: () => {
      // Refetch to get server state with real IDs and complete data
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
    }
  })
}

/**
 * Mutation hook for updating calendar events
 * Implements optimistic updates with rollback on error
 */
export function useUpdateEvent(): UseMutationResult<
  CalendarEvent,
  Error,
  { eventId: string; payload: UpdateEventPayload }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, payload }) => calendarApi.updateEvent(eventId, payload),
    onMutate: async ({ eventId, payload }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['calendar-events'] })

      // Snapshot current value
      const previousEvents = queryClient.getQueriesData({ queryKey: ['calendar-events'] })

      // Optimistically update
      queryClient.setQueriesData(
        { queryKey: ['calendar-events'] },
        (old: CalendarEvent[] | undefined) => {
          if (!old) return old

          return old.map(event =>
            event.eventId === eventId
              ? {
                  ...event,
                  ...payload,
                  updatedAt: new Date().toISOString()
                }
              : event
          )
        }
      )

      return { previousEvents }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousEvents) {
        context.previousEvents.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSuccess: () => {
      // Refetch to confirm server state
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
    }
  })
}

/**
 * Mutation hook for deleting calendar events
 * Implements optimistic updates with rollback on error
 */
export function useDeleteEvent(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: calendarApi.deleteEvent,
    onMutate: async (eventId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['calendar-events'] })

      // Snapshot current value
      const previousEvents = queryClient.getQueriesData({ queryKey: ['calendar-events'] })

      // Optimistically remove event
      queryClient.setQueriesData(
        { queryKey: ['calendar-events'] },
        (old: CalendarEvent[] | undefined) => {
          if (!old) return old
          return old.filter(event => event.eventId !== eventId)
        }
      )

      return { previousEvents }
    },
    onError: (_err, _eventId, context) => {
      // Rollback on error
      if (context?.previousEvents) {
        context.previousEvents.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSuccess: () => {
      // Refetch to confirm server state
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
    }
  })
}
